import { DateFragment, DateTime, Duration, Time, TimezoneRegion } from "@tento-chrono";
import { Div } from "@tento-ui/component-builtin";
import { ComponentIcon, LonaIcons } from "@tento-ui/ui/icons";
import { LineChartBody } from "@tento-ui/components/line-chart/line-chart-body";
import { SpanChart } from "@tento-ui/components/span/components/span-chart";
import { SpanColumn } from "@tento-ui/components/span/components/span-column";
import { Span as ContentSpan } from "@tento-ui/components/span/span";
import { ChartViewModel } from "@tento-lona/platform";
import { Json, RowId, type Option } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import {
  CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { ChartRendererProvider } from "@tento-lona/sheets-ui/sheet/chart-renderer-provider.ts";
import { DefaultBinderHelpers } from "@tento-lona/sheets-ui/sheet/binder/default-binder-helpers.ts";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { CHART_ALIAS_SPEC } from "@tento-lona/sheets/platform";
import { ChartPointerStore, GraphData } from "@tento-lona/sheets/internal-ui";
import { castData } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * `alias.chart` — a data-series subcolumn for the `timeline[column]`
 * (vertical, time-down) surface. The alias owns ONE `SpanChart` per day
 * column and the shared coordinate space (series + value axis); its mark
 * children (`timeline[column].points` / `.regression-curve` /
 * `.regression-band`, authored as `data-renderer` rows) each contribute a
 * `LineChartBody.Renderer` that composites into that chart.
 *
 * This is the vertical mirror of `DataAliasPlugin` + `CanvasLayoutPlugin`:
 * where the canvas layout walks `contentProviders.canvas.rendererForRow`,
 * this discovers its mark children through
 * `contentProviders.chart.rendererForRow` (each mark is a first-class
 * `timeline[column].{points,regression-curve,regression-band}` renderer
 * plugin). Data is the seeded synthetic series (`GraphData.Generated`),
 * so a row renders in isolation with no backend.
 */
export class ChartAliasPlugin
  implements RenderPlugin.Alias<HTMLDivElement, SheetLegend.DefaultRow>
{
  static readonly spec: PerTypeSpec = CHART_ALIAS_SPEC;

  readonly role = "alias";
  readonly id = "alias.chart";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ComponentIcon);

  /** One synthetic series per (seed, axis) — reused across day windows. */
  #collections = new Map<string, GraphData.Collection>();

  constructor(public readonly makeRowOptions: RowOptionsElement.Factory) {}

  makeCell(): HTMLDivElement {
    return Div.make();
  }

  bindCell(_$cell: HTMLDivElement, _context: BindCellContext): void {}

  #calendarProvider: CalendarSubcolumn.Provider = ((self) => ({
    subcolumnFromRow(
      _sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
      pluginManager: SheetDelegate.PluginManager,
    ): CalendarSubcolumn {
      const attrs = row.attributes();
      const width = Json.string(attrs, "width");
      const seed = Json.number(attrs, "seed") ?? 0;
      const renderers = self.#renderersFromChildren(row, pluginManager);

      return {
        rowId: RowId.fromLocalId(row.id),
        width: typeof width === "string"
          ? { type: "preferred", width }
          : { type: "flex" },
        makeSpans: ({ windowed }) =>
          self.#makeSpansForWindow(seed, renderers, windowed),
        makeLayout: () => {
          const $column = SpanColumn.make();
          // SpanColumn's 4px margin-right is the event-chip gutter
          // (span-column.ts `:host(:not([sharing]))`); chart series
          // lanes tile flush, so it would read as white stripes.
          $column.style.marginRight = "0px";
          return $column;
        },
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };

  /**
   * Discover each mark child's `LineChartBody.Renderer` via
   * `contentProviders.chart`, in child order. Mirrors
   * `CanvasLayoutPlugin.collectCanvasRenderers`: walk `row.children()`,
   * resolve each child's capability plugins, and take the first that
   * exposes `contentProviders.chart` and returns a non-null renderer.
   * Unknown/foreign marks (no chart provider, or a provider that
   * declines) are silently skipped.
   */
  #renderersFromChildren(
    row: SheetRow.Joined.Handle,
    pluginManager: SheetDelegate.PluginManager,
  ): LineChartBody.Renderer[] {
    const out: LineChartBody.Renderer[] = [];
    for (const child of row.children()) {
      for (
        const plugin of DefaultBinderHelpers.resolveRowCapabilityPlugins(
          child,
          pluginManager,
        )
      ) {
        const provider = (plugin as ChartCapabilityPlugin).contentProviders
          ?.chart;
        if (!provider) continue;
        const renderer = provider.rendererForRow(child);
        if (!renderer) continue;
        out.push(renderer);
        break; // first match per child
      }
    }
    return out;
  }

  #collectionFor(seed: number): GraphData.Collection {
    const key = String(seed);
    let collection = this.#collections.get(key);
    if (!collection) {
      // The `Generated` procedural signal is `perlin + 0.5` → roughly [0, 1]
      // centered on 0.5. HALF_UNIT ([-0.5, 0.5]) puts 0.5 at the top edge, so
      // every value ≥0.5 clips to the top; frame [-0.25, 1.25] so the wave
      // fills the column. A per-row y-axis override lands with real data
      // (decision 4).
      const axis: ChartViewModel.AxisRange = {
        min: castData(-0.25),
        max: castData(1.25),
        center: castData(0.5),
      };
      collection = GraphData.Collection.makeFromSpec(
        [{ key: "default", type: "debug", seed, yRange: axis }],
        axis,
      );
      this.#collections.set(key, collection);
    }
    return collection;
  }

  async #makeSpansForWindow(
    seed: number,
    renderers: LineChartBody.Renderer[],
    windowed: DateFragment.Windowed,
  ): Promise<CalendarSubcolumnSpan[]> {
    const data = this.#collectionFor(seed);

    const start = windowed.fragment.date
      .withTime(windowed.validHours?.start)
      .withTz(windowed.fragment.tz);
    const time = new DateTime.Range(
      start,
      start.add(windowed.validHours?.duration ?? Duration.Time.DAY1),
    );
    const xRange = {
      start: Math.floor(time.start.mse / Time.MS_PER_HR),
      end: Math.ceil(time.end.mse / Time.MS_PER_HR),
    };

    const defaultRow = data.rows.get("default");
    if (defaultRow) {
      await defaultRow.data.populate(xRange, xRange, [], "alias-chart", null);
    }

    return [makeChartSpan(data, time, renderers)];
  }

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}

  onResize(_$cell: HTMLDivElement, _context: BindCellContext): void {}

  onViewportColumnsChanged(): void {}

  onRenderedColumnsWillChange(): void {}

  loadData = null;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }
}

/** Structural view of a plugin that contributes a chart-mark renderer. */
type ChartCapabilityPlugin = {
  contentProviders?: Option<{
    chart?: ChartRendererProvider;
  }>;
};

function makeChartSpan(
  data: GraphData.Collection,
  time: DateTime.Range,
  renderer: LineChartBody.Renderer[],
): CalendarSubcolumnSpan {
  const xRange = {
    start: Math.floor(time.start.mse / Time.MS_PER_HR),
    end: Math.ceil(time.end.mse / Time.MS_PER_HR),
  };

  const $e = SpanChart.make();
  $e.toggleAttribute("hide-icon", true);
  $e.style.setProperty("--current-time-mse", Date.now().toString());
  $e.style.setProperty("--start", time.start.mse.toString());
  $e.style.setProperty("--end", time.end.mse.toString());

  const vm = new ChartViewModel(new ChartPointerStore(), data, true);
  $e.bindGraph({ xRange, vm, renderer, yPadding: 2 }, time.duration.toHrsF * 48);

  return {
    id: `${time.start.toString()}-${time.end.toString()}`,
    bindContent: ContentSpan.staticContent($e),
    start: time.start,
    end: time.end,
    pointerEvents: "none",
    item: null,
    callbacks: null,
  };
}
